import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import EmotionInsightDisplay from "./EmotionInsightDisplay.js";
import ReflectionRichText from "./ReflectionRichText.js";
import { ACTIONS_ROUTE, DEEP_REFLECTION_ROUTE } from "../constants/routes.js";
import { getNetlifyFunctionsBase } from "../utils/netlifyFunctions.js";
import { EMOTION_CATEGORIES, EMOTION_ATLAS, getCategoryForEmotion, getEmotionMeta } from "../constants/emotions.js";
import { getCbtPatternLearnMore } from "../constants/cbtPatternLearnMore.js";
import { ACTION_CATEGORY_KEYS, ACTION_CATEGORIES } from "../constants/actionCategories.js";
import { QC_UI_STRINGS, QC_LANG_OPTIONS } from "../constants/quickClarityUiStrings.js";
import { useInsightTranslation } from "../hooks/useInsightTranslation.js";
import { getQuickClaritySuggestedActions } from "../utils/quickClaritySuggestedActions.js";

function allAtlasLabels() {
  const labels = [];
  for (const cat of EMOTION_CATEGORIES) {
    for (const e of EMOTION_ATLAS[cat] || []) {
      labels.push(e.key);
    }
  }
  return labels;
}

const ATLAS_LABELS = allAtlasLabels();

function QcSection({ step, title, children, className = "" }) {
  return (
    <section className={`qc-section${className ? ` ${className}` : ""}`}>
      <header className="qc-section__header">
        {step != null ? <span className="qc-section__step">{step}</span> : null}
        <h3 className="qc-section__title">{title}</h3>
      </header>
      <div className="qc-section__body">{children}</div>
    </section>
  );
}

/**
 * Quick Clarity session result: numbered sections for scanability.
 */
const QuickClarityResult = ({ insight, verifiedEmail, onConfirmationChange }) => {
  const n = insight || {};
  const { displayLang, setDisplayLang, tx, txList, isTranslating, translationError, retryTranslation } =
    useInsightTranslation(n);
  const t = QC_UI_STRINGS[displayLang] || QC_UI_STRINGS.en;

  const insightOptions = useMemo(
    () => [
      { value: "yes", label: t.insightYes },
      { value: "partly", label: t.insightPartly },
      { value: "not_quite", label: t.insightNotQuite }
    ],
    [t.insightNotQuite, t.insightPartly, t.insightYes]
  );

  const patternOptions = useMemo(
    () => [
      { value: "yes", label: t.patternYes },
      { value: "partly", label: t.patternPartly },
      { value: "not_really", label: t.patternNotReally }
    ],
    [t.patternNotReally, t.patternPartly, t.patternYes]
  );

  const dueOptions = useMemo(
    () => [
      { value: "tomorrow", label: t.dueTomorrow },
      { value: "three_days", label: t.dueThreeDays },
      { value: "next_week", label: t.dueNextWeek }
    ],
    [t.dueNextWeek, t.dueThreeDays, t.dueTomorrow]
  );

  const emotionStatusLabel = useMemo(
    () => ({
      confirmed: t.emotionConfirmed,
      corrected: t.emotionCorrected
    }),
    [t.emotionConfirmed, t.emotionCorrected]
  );

  const [confirmation, setConfirmation] = useState(n.confirmation || {});
  const [saving, setSaving] = useState(false);
  const [correctingEmotion, setCorrectingEmotion] = useState(false);
  const [correctedLabel, setCorrectedLabel] = useState(
    n.confirmation?.emotion_primary_corrected || n.emotions?.primary?.label || ""
  );
  const [actionText, setActionText] = useState(n.user_action?.text || "");
  const [duePreset, setDuePreset] = useState("tomorrow");
  const [actionCategory, setActionCategory] = useState(n.user_action?.category || "job");
  const [reminderOptIn, setReminderOptIn] = useState(true);
  const [actionSaved, setActionSaved] = useState(!!n.user_action?.text);
  const [actionError, setActionError] = useState("");
  const [actionSkipped, setActionSkipped] = useState(n.user_action?.status === "skipped");

  const primaryLabel =
    confirmation?.emotion_primary?.status === "corrected" && confirmation.emotion_primary_corrected
      ? confirmation.emotion_primary_corrected
      : n.emotions?.primary?.label || n.sentiment;
  const primaryCategory = primaryLabel
    ? getCategoryForEmotion(primaryLabel) || n.emotion_category
    : null;
  const beneath = Array.isArray(n.emotions?.beneath) ? n.emotions.beneath : [];
  const facts = Array.isArray(n.observed_or_reported_facts) ? n.observed_or_reported_facts : [];
  const unknowns = Array.isArray(n.unknowns) ? n.unknowns : [];
  const pattern = n.possible_cbt_pattern;
  const learnMore = pattern?.key ? getCbtPatternLearnMore(pattern.key) : null;
  const showActionPrompt =
    !!confirmation.insight_status && !actionSaved && !actionSkipped && !!n.primary_insight;

  const showUnderstanding = !!n.understanding;
  const showFacts = facts.length > 0;
  const showInterpretation = !!n.user_interpretation;
  const showUnknowns = unknowns.length > 0;
  const showInsight = !!n.primary_insight;
  const showClarify = !showInsight && !!n.clarifying_question;
  const showPattern = !!pattern;
  const showAction = showActionPrompt;
  const showActionSaved = actionSaved && !showActionPrompt;

  const stepFor = (() => {
    const flags = [
      showUnderstanding,
      true, // emotions always
      showFacts,
      showInterpretation,
      showUnknowns,
      showInsight || showClarify,
      showPattern,
      showAction,
      showActionSaved
    ];
    let nStep = 0;
    return flags.map((on) => (on ? ++nStep : null));
  })();
  const [
    stepUnderstanding,
    stepEmotions,
    stepFacts,
    stepInterpretation,
    stepUnknowns,
    stepInsight,
    stepPattern,
    stepAction,
    stepActionSaved
  ] = stepFor;

  const persist = async (next) => {
    setConfirmation(next);
    onConfirmationChange?.(next);
    if (!verifiedEmail || !n.id) return;
    setSaving(true);
    try {
      await fetch(`${getNetlifyFunctionsBase()}/.netlify/functions/updateInsightConfirmation`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          insightId: n.id,
          email: verifiedEmail,
          confirmation: next
        })
      });
    } catch (err) {
      console.error("Unable to save confirmation", err);
    } finally {
      setSaving(false);
    }
  };

  const setInsightStatus = (insight_status) => {
    persist({ ...confirmation, insight_status });
  };

  const setPatternStatus = (pattern_status) => {
    persist({ ...confirmation, pattern_status });
  };

  const confirmEmotion = () => {
    setCorrectingEmotion(false);
    persist({
      ...confirmation,
      emotion_primary: { status: "confirmed" },
      emotion_primary_corrected: null
    });
  };

  const saveCorrectedEmotion = () => {
    const label = String(correctedLabel || "").trim();
    if (!label) return;
    setCorrectingEmotion(false);
    persist({
      ...confirmation,
      emotion_primary: { status: "corrected" },
      emotion_primary_corrected: label
    });
  };

  const saveAction = async () => {
    const text = String(actionText || "").trim();
    if (!text || !verifiedEmail || !n.id) {
      setActionError("Write one concrete next step to save.");
      return;
    }
    setActionError("");
    setSaving(true);
    try {
      const response = await fetch(`${getNetlifyFunctionsBase()}/.netlify/functions/updateInsightUserAction`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          insightId: n.id,
          email: verifiedEmail,
          text,
          status: "open",
          due_preset: duePreset,
          category: actionCategory,
          reminder_opt_in: reminderOptIn
        })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data?.error || "Unable to save action.");
      setActionSaved(true);
    } catch (err) {
      setActionError(err?.message || "Unable to save action.");
    } finally {
      setSaving(false);
    }
  };

  const skipAction = async () => {
    setActionSkipped(true);
    if (!verifiedEmail || !n.id) return;
    try {
      await fetch(`${getNetlifyFunctionsBase()}/.netlify/functions/updateInsightUserAction`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          insightId: n.id,
          email: verifiedEmail,
          status: "skipped"
        })
      });
    } catch (_) {
      /* local skip still stands */
    }
  };

  const translatedFacts = txList("observedFacts", facts);
  const translatedUnknowns = txList("unknowns", unknowns);
  const translatedBeneath = txList("emotionBeneath", beneath.map((b) => b.label));
  const suggestedActions = getQuickClaritySuggestedActions(n);
  const translatedSuggestions = txList("suggestedActions", suggestedActions);

  return (
    <div className="insight-summary quick-clarity-result" lang={displayLang}>
      <header className="qc-result-header">
        <div className="qc-result-header__top">
          <div>
            <p className="qc-result-header__eyebrow">{t.quickClarity}</p>
            <h2 className="qc-result-header__title">{t.sessionSummary}</h2>
          </div>
          <div
            className="history-lang-toggle qc-lang-toggle"
            role="group"
            aria-label={t.languageGroupAria}
          >
            {QC_LANG_OPTIONS.map(({ code, label }) => (
              <button
                key={code}
                type="button"
                className={`history-lang-toggle__btn${displayLang === code ? " history-lang-toggle__btn--active" : ""}`}
                aria-pressed={displayLang === code}
                onClick={() => setDisplayLang(code)}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
        <p className="qc-result-header__lede">{t.sessionLede}</p>
        {isTranslating ? <p className="qc-muted">{t.translating}</p> : null}
        {translationError ? (
          <div className="history-card__translate-error-wrap">
            <p className="history-card__translate-error">{t.translationFailed}</p>
            <button type="button" className="history-card__retry-translate" onClick={retryTranslation}>
              {t.retryTranslation}
            </button>
          </div>
        ) : null}
        {displayLang !== "en" ? <p className="history-card__translate-hint">{t.translateHint}</p> : null}
      </header>

      <div className="qc-result-stack">
        {showUnderstanding ? (
          <QcSection step={stepUnderstanding} title={t.understanding}>
            <div className="qc-prose">
              <ReflectionRichText text={tx("understanding", n.understanding)} />
            </div>
          </QcSection>
        ) : null}

        <QcSection step={stepEmotions} title={t.emotions}>
          <div className="qc-emotion-block">
            <h4 className="qc-subsection-title">{t.mainEmotion}</h4>
            {primaryLabel ? (
              <div className="qc-emotion-panel">
                <EmotionInsightDisplay
                  sentiment={primaryLabel}
                  emotionCategory={primaryCategory}
                  emotionLabel={t.emotion}
                  categoryLabel={t.category}
                  translatedEmotionLabel={tx("emotionLabel", primaryLabel)}
                  translatedCategory={tx("emotionCategory", primaryCategory)}
                  translatedMeaning={tx(
                    "emotionMeaning",
                    getEmotionMeta(primaryLabel)?.meaning || ""
                  )}
                  className="qc-emotion-display"
                />
              </div>
            ) : (
              <p className="qc-muted">{t.noMainEmotion}</p>
            )}
            <div className="qc-confirm-block">
              <p className="qc-confirm-prompt">{t.confirmMainEmotion}</p>
              <div className="qc-confirm-row" role="group" aria-label={t.confirmMainEmotion}>
                <button type="button" className="qc-confirm-btn" onClick={confirmEmotion}>
                  {t.confirmEmotion}
                </button>
                <button type="button" className="qc-confirm-btn" onClick={() => setCorrectingEmotion(true)}>
                  {t.correctEmotion}
                </button>
              </div>
              {correctingEmotion ? (
                <div className="qc-correct-emotion">
                  <label htmlFor="qc-emotion-correct">{t.correctedLabel}</label>
                  <select
                    id="qc-emotion-correct"
                    value={correctedLabel}
                    onChange={(e) => setCorrectedLabel(e.target.value)}
                  >
                    {ATLAS_LABELS.map((label) => (
                      <option key={label} value={label}>
                        {label}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    className="qc-confirm-btn qc-confirm-btn--primary"
                    onClick={saveCorrectedEmotion}
                  >
                    {t.saveLabel}
                  </button>
                </div>
              ) : null}
              {confirmation?.emotion_primary?.status ? (
                <p className="qc-status" role="status">
                  {emotionStatusLabel[confirmation.emotion_primary.status] ||
                    confirmation.emotion_primary.status}
                </p>
              ) : null}
            </div>
          </div>

          {beneath.length > 0 ? (
            <div className="qc-emotion-block qc-emotion-block--beneath">
              <h4 className="qc-subsection-title">{t.mayAlsoBePresent}</h4>
              <ul className="qc-beneath-list">
                {beneath.map((b, index) => (
                  <li key={b.label}>{translatedBeneath[index] || b.label}</li>
                ))}
              </ul>
            </div>
          ) : null}
        </QcSection>

        {showFacts ? (
          <QcSection step={stepFacts} title={t.whatHappened}>
            <p className="qc-section-note">{t.factsNote}</p>
            <ul className="qc-fact-list">
              {translatedFacts.map((f) => (
                <li key={f}>{f}</li>
              ))}
            </ul>
          </QcSection>
        ) : null}

        {showInterpretation ? (
          <QcSection step={stepInterpretation} title={t.yourInterpretation}>
            <p className="qc-section-note">{t.interpretationNote}</p>
            <div className="qc-prose">
              <ReflectionRichText text={tx("userInterpretation", n.user_interpretation)} />
            </div>
          </QcSection>
        ) : null}

        {showUnknowns ? (
          <QcSection step={stepUnknowns} title={t.unknowns}>
            <ul className="qc-fact-list">
              {translatedUnknowns.map((u) => (
                <li key={u}>{u}</li>
              ))}
            </ul>
          </QcSection>
        ) : null}

        {showInsight ? (
          <QcSection step={stepInsight} title={t.primaryInsight} className="qc-section--insight">
            <div className="qc-prose qc-prose--emphasis">
              <ReflectionRichText text={tx("primaryInsight", n.primary_insight)} />
            </div>
            <div className="qc-confirm-block">
              <p className="qc-confirm-prompt">{t.insightConfirm}</p>
              <div className="qc-confirm-row" role="group" aria-label={t.insightConfirm}>
                {insightOptions.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    className={`qc-confirm-btn${confirmation.insight_status === opt.value ? " qc-confirm-btn--active" : ""}`}
                    onClick={() => setInsightStatus(opt.value)}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
          </QcSection>
        ) : showClarify ? (
          <QcSection step={stepInsight} title={t.needDetail}>
            <div className="qc-prose">
              <p>{tx("clarifyingQuestion", n.clarifying_question)}</p>
            </div>
          </QcSection>
        ) : null}

        {showPattern ? (
          <QcSection step={stepPattern} title={t.thinkingPattern}>
            <p className="qc-pattern-label">{tx("patternLabel", pattern.label)}</p>
            {pattern.why_it_may_apply ? (
              <div className="qc-prose">
                <p>{tx("patternWhy", pattern.why_it_may_apply)}</p>
              </div>
            ) : null}
            {learnMore ? (
              <details className="qc-learn-more">
                <summary>{t.learnMore}</summary>
                <div className="qc-learn-more__body">
                  <p>
                    <strong>{t.definition}:</strong> {learnMore.definition}
                  </p>
                  <p>
                    <strong>{t.example}:</strong> {learnMore.example}
                  </p>
                  <p>
                    <strong>{t.thoughtCheck}:</strong> {learnMore.thoughtCheck}
                  </p>
                  <p>
                    <strong>{t.questionToConsider}:</strong> {learnMore.question}
                  </p>
                </div>
              </details>
            ) : null}
            <div className="qc-confirm-block">
              <p className="qc-confirm-prompt">{t.patternConfirm}</p>
              <div className="qc-confirm-row" role="group" aria-label={t.patternConfirm}>
                {patternOptions.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    className={`qc-confirm-btn${confirmation.pattern_status === opt.value ? " qc-confirm-btn--active" : ""}`}
                    onClick={() => setPatternStatus(opt.value)}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
          </QcSection>
        ) : null}

        {showAction ? (
          <QcSection step={stepAction} title={t.actionTitle} className="qc-section--action">
            <p className="qc-section-note">{t.actionNote}</p>
            {suggestedActions.length > 0 ? (
              <div className="qc-action-suggestions">
                <p className="qc-confirm-prompt">{t.suggestedActions}</p>
                <ul className="qc-action-suggestions__list">
                  {suggestedActions.map((suggestion, index) => {
                    const label = translatedSuggestions[index] || suggestion;
                    const active = actionText.trim() === suggestion.trim();
                    return (
                      <li key={`${index}-${suggestion.slice(0, 24)}`}>
                        <button
                          type="button"
                          className={`qc-action-suggestion${active ? " qc-action-suggestion--active" : ""}`}
                          onClick={() => {
                            setActionText(suggestion);
                            setActionError("");
                          }}
                        >
                          {label}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ) : null}
            <textarea
              className="qc-action-input"
              rows={3}
              value={actionText}
              onChange={(e) => setActionText(e.target.value)}
              placeholder={t.actionPlaceholder}
            />
            <p className="qc-confirm-prompt">{t.category}</p>
            <div className="qc-confirm-row" role="group" aria-label="Action category">
              {ACTION_CATEGORY_KEYS.map((key) => (
                <button
                  key={key}
                  type="button"
                  className={`qc-confirm-btn${actionCategory === key ? " qc-confirm-btn--active" : ""}`}
                  onClick={() => setActionCategory(key)}
                >
                  {ACTION_CATEGORIES[key].label}
                </button>
              ))}
            </div>
            <p className="qc-confirm-prompt">{t.dueDate}</p>
            <div className="qc-confirm-row" role="group" aria-label={t.dueDate}>
              {dueOptions.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  className={`qc-confirm-btn${duePreset === opt.value ? " qc-confirm-btn--active" : ""}`}
                  onClick={() => setDuePreset(opt.value)}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            <label className="qc-reminder-opt">
              <input
                type="checkbox"
                checked={reminderOptIn}
                onChange={(e) => setReminderOptIn(e.target.checked)}
              />
              {t.reminderOptIn}
            </label>
            {actionError ? <p className="login-error">{actionError}</p> : null}
            <div className="qc-confirm-row">
              <button
                type="button"
                className="qc-confirm-btn qc-confirm-btn--primary"
                onClick={saveAction}
                disabled={saving}
              >
                {t.saveAction}
              </button>
              <button type="button" className="qc-confirm-btn" onClick={skipAction}>
                {t.skip}
              </button>
            </div>
          </QcSection>
        ) : null}

        {showActionSaved ? (
          <QcSection step={stepActionSaved} title={t.actionSection}>
            <p className="qc-status" role="status">
              {t.actionSaved}{" "}
              <Link to={ACTIONS_ROUTE}>{t.reviewActions}</Link>.
            </p>
          </QcSection>
        ) : null}

        {saving ? <p className="qc-muted">{t.saving}</p> : null}

        <footer className="qc-result-footer">
          <Link className="qc-deep-link" to={DEEP_REFLECTION_ROUTE}>
            {t.deepLink}
          </Link>
        </footer>
      </div>
    </div>
  );
};

export default QuickClarityResult;
